import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/supabase/client';

type CreditRow = Record<string, unknown>;

type SignalLike = { aborted?: boolean };

function extractAvailable(row: CreditRow | null | undefined): number {
  if (!row || typeof row !== 'object') return 0;
  const candidates = [
    'available_credits',
    'balance',
    'remaining_credits',
    'remaining',
    'credits',
    'balance_credits',
    'credit_balance',
  ];

  for (const key of candidates) {
    if (key in row) {
      const value = Number(row[key]);
      if (Number.isFinite(value)) {
        return value;
      }
    }
  }

  return 0;
}

function sumAvailable(rows: CreditRow | CreditRow[] | null | undefined): number {
  if (!rows) return 0;
  const list = Array.isArray(rows) ? rows : [rows];
  return list.reduce((total, row) => {
    const amount = extractAvailable(row);
    return total + (amount > 0 ? amount : 0);
  }, 0);
}

export function useTeacherCredits(teacherId: string | null | undefined) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [available, setAvailable] = useState(0);

  const refresh = useCallback(
    async ({ signal }: { signal?: SignalLike } = {}) => {
      const apply = (updater: () => void) => {
        if (signal?.aborted) return;
        updater();
      };

      if (!teacherId) {
        apply(() => {
          setAvailable(0);
          setError(null);
          setLoading(false);
        });
        return 0;
      }

      apply(() => {
        setLoading(true);
        setError(null);
      });

      // Try v_user_credit_balances first, fall back to credit_accounts
      let total = 0;
      const { data, error: queryError } = await (supabase as any)
        .from('v_user_credit_balances')
        .select('*')
        .eq('user_id', teacherId);

      if (!queryError && data) {
        total = sumAvailable(data);
      }

      // If view returned 0 or errored, check credit_accounts as fallback
      if (total === 0) {
        const { data: acctData } = await (supabase as any)
          .from('credit_accounts')
          .select('balance_credits')
          .eq('user_id', teacherId)
          .maybeSingle();
        if (acctData?.balance_credits != null) {
          total = Number(acctData.balance_credits) || 0;
        }
      }
      apply(() => {
        setAvailable(Math.max(0, total));
        setLoading(false);
      });
      return total;
    },
    [teacherId],
  );

  useEffect(() => {
    const controller = { aborted: false };
    refresh({ signal: controller }).catch(() => {
      /* handled in refresh */
    });
    return () => {
      controller.aborted = true;
    };
  }, [refresh]);

  const adjust = useCallback((delta: number) => {
    setAvailable((prev) => {
      const next = Number(prev) + Number(delta || 0);
      return Number.isFinite(next) ? Math.max(0, next) : prev;
    });
  }, []);

  return { loading, error, available, refresh, adjust };
}

export default useTeacherCredits;
